"use client"

import { MailOutlined, MessageOutlined } from "@ant-design/icons"
import { ComponentConfig } from "@measured/puck"
import { twMerge } from "tailwind-merge"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"

export type ContactHeroIcon = "phone" | "mail" | "consult"

export interface ContactHeroItem {
  iconType: ContactHeroIcon
  label: string
  value: string
  ctaText: string
  ctaLink: string
}

export interface ContactHeroProps extends CommonStylesProps {
  title?: string
  description?: string
  connectTitle?: string
  items?: ContactHeroItem[]
}

const PhoneIcon = (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.34a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.74-1.27a2 2 0 0 1 2.11-.45c.74.34 1.53.57 2.34.7A2 2 0 0 1 22 16.92z" />
  </svg>
)

const iconMap: Record<ContactHeroIcon, React.ReactNode> = {
  phone: PhoneIcon,
  mail: <MailOutlined />,
  consult: <MessageOutlined />,
}

export const RenderConfig: ComponentConfig<ContactHeroProps> = {
  render: ({ styles, responsiveType, className, title, description, connectTitle, items }) => {
    const id = `contact-hero-${Math.random().toString(36).substr(2, 9)}`

    const mergedStyles = {
      mobile: { ...styles?.mobile },
      tablet: { ...styles?.tablet },
      desktop: { ...styles?.desktop },
    }

    const responsiveCSS = generateResponsiveCSS(id, mergedStyles || {}, responsiveType)

    return (
      <>
        {responsiveCSS}
        <div className={`${id} ${twMerge(className)} w-full bg-[#F3FBFA] rounded-xl`}>
          <div className="max-w-[1440px] mx-auto px-4 py-10 sm:px-[120px] sm:py-16">
            <div className="text-center">
              <h1 className="text-[#08395C] font-bold text-2xl leading-8 sm:text-[40px] sm:leading-[48px]">
                {title}
              </h1>
              {description && (
                <p className="mx-auto mt-4 max-w-[800px] text-sm leading-6 text-[#5B5F6B] sm:text-base sm:leading-7">
                  {description}
                </p>
              )}
            </div>

            {connectTitle && <h2 className="sr-only">{connectTitle}</h2>}

            {items && items.length > 0 && (
              <div className="mx-auto mt-8 grid max-w-[1024px] grid-cols-1 divide-y divide-[#E1E1E2] overflow-hidden rounded-lg border border-[#E1E1E2] sm:mt-10 sm:grid-cols-3 sm:divide-y-0 sm:divide-x">
                {items.map((item, index) => (
                  <div
                    key={index}
                    className={`flex flex-col gap-3 px-6 py-7 sm:px-7 sm:py-9 ${index === 1 ? "bg-[#F1FAF9]" : "bg-white"}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg text-[#00A19A]">{iconMap[item.iconType] ?? iconMap.phone}</span>
                      <span className="text-xs font-semibold uppercase tracking-wide leading-5">{item.label}</span>
                    </div>
                    {item.value && (
                      <p className="text-base font-semibold leading-6 text-[#08395C] break-words">{item.value}</p>
                    )}
                    {item.ctaText && (
                      <a
                        href={item.ctaLink || "#"}
                        className="mt-1 inline-flex items-center gap-1 text-sm font-bold text-[#00A19A] hover:text-[#00847F] transition-colors"
                      >
                        {item.ctaText}
                        <span aria-hidden="true">→</span>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </>
    )
  },
}
